import Table from "@/ui/table";
import { Index, Price, Time, Trend, Value } from "@/ui/formats";
import { secondsAgoFromTimestamp } from "@/helpers";
import { IconComponent } from "@/ui/icon";
import { useEffect, useMemo, useRef, useState } from "react";
import BalancePercentage from "@/ui/balance-percentage";
import TokenIcon from "@/ui/icon/token-icon";
// import PositionsSellSend from "./positions-sell-send";
import LoadingIndicator from "@/ui/loading-indicator";
import { usePositionsFilter } from "@/providers/positions-filter/positions-filter";
import Pagination from "../pagination/pagination";
import { CodexChainId, useWallet, WalletType } from "@/providers/wallet";
import { useGetBotPositions } from "@/api/positions/positions-bot";
import IconButton from "@/ui/icon-button";
import { Link } from "react-router";
import TokenSellModal from "../filters-modals/token-sell-modal";
import TokenSendModal from "../filters-modals/token-send-modal";
import { WalletPosition } from "@/api/positions/types";

const ITEMS_PER_PAGE = 50;

const PositionsTableData = ({
  currentPage,
  setCurrentPage,
}: {
  currentPage: number;
  setCurrentPage: (page: number) => void;
}) => {
  const { userId, currentWallet } = useWallet();

  const { filterPositions, sort } = usePositionsFilter();

  const { data, isLoading, isError } = useGetBotPositions(
    userId,
    currentWallet
  );

  const [sellModalOpen, setSellModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] =
    useState<WalletPosition | null>(null);

  const [sendModalOpen, setSendModalOpen] = useState(false);

  const positions = useMemo(
    () => filterPositions(data ?? []),
    [data, filterPositions]
  );

  const paginatedPositions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return positions.slice(start, end);
  }, [positions, currentPage]);

  const hasNextPage = currentPage * ITEMS_PER_PAGE < positions.length;
  const hasPreviousPage = currentPage > 1;

  const chain =
    currentWallet.walletType === WalletType.ETH
      ? CodexChainId.ETH
      : CodexChainId.SOL;

  useEffect(() => {
    setCurrentPage(1);
  }, [positions.length, setCurrentPage, sort]);

  return (
    <>
      {isLoading ? (
        <Table.Row>
          <Table.Column className="!p-0" colSpan={7}>
            <LoadingIndicator />
          </Table.Column>
        </Table.Row>
      ) : isError ? (
        <Table.Row>
          <Table.Column colSpan={7} className="!p-[0.625rem]">
            Something went wrong while loading your wallet positions. Please try
            again later.
          </Table.Column>
        </Table.Row>
      ) : positions.length === 0 ? (
        <Table.Row>
          <Table.Column colSpan={7} className="!p-[0.625rem]">
            <span>No positions found for this wallet.</span>
          </Table.Column>
        </Table.Row>
      ) : (
        paginatedPositions.map((position, index) => {
          const i = (currentPage - 1) * ITEMS_PER_PAGE + index;

          return (
            <Table.Row
              key={i}
              className="h-[1.875rem] relative gradient-box before:py-[0.0625rem] before:hover:bg-gradient-100 hover:bg-table-hover"
              // expandedSection={<PositionsSellSend position={position} />}
            >
              <Table.Column className="w-[1.875rem] h-[1.875rem] flex justify-center items-center">
                <Index value={i + 1} />
              </Table.Column>
              <Table.Column>
                <div className="flex items-center gap-x-[0.3125rem]">
                  <IconComponent
                    name={currentWallet.walletType}
                    className="w-[0.9375rem] h-[0.9375rem] shrink-0"
                  />
                  <div className="flex-row flex gap-x-[0.3125rem] items-center text-xs">
                    <div className="shrink-0">
                      <TokenIcon
                        tokenName={position.token_name?.toLowerCase() ?? ""}
                        logo={position.logo}
                      />
                    </div>
                    <p className="font-semibold capitalize text-white">
                      {position.token_symbol}
                    </p>
                    <div className="flex items-center gap-x-[0.188rem]">
                      <Link
                        to={`/screener/${position.token_address}:${chain}`}
                        target="_blank"
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        <IconButton
                          withHover={true}
                          withBorder={false}
                          name="barChart"
                          variant="circle"
                          className="w-[0.938rem] h-[0.938rem] bg-black rounded-full flex justify-center items-center"
                          iconClassName="w-[60%] h-[60%]"
                        />
                      </Link>
                      <IconButton
                        withHover={true}
                        withBorder={false}
                        name="dollarSign"
                        variant="circle"
                        onClick={() => {
                          setSelectedPosition(position);
                          setSellModalOpen(true);
                        }}
                        className="w-[0.938rem] h-[0.938rem] bg-black rounded-full flex justify-center items-center"
                        iconClassName="w-[65%] h-[65%]"
                      />
                      <IconButton
                        withHover={true}
                        withBorder={false}
                        name="send"
                        variant="circle"
                        onClick={() => {
                          setSelectedPosition(position);
                          setSendModalOpen(true);
                        }}
                        className="w-[0.938rem] h-[0.938rem] bg-black rounded-full flex justify-center items-center"
                        iconClassName="w-[60%] h-[60%]"
                      />
                    </div>
                  </div>
                </div>
              </Table.Column>
              <Table.Column>
                {position.invested ? <Price value={position.invested} /> : "-"}
              </Table.Column>
              <Table.Column>
                {position.net_pnl ? (
                  <Value
                    value={position.net_pnl}
                    withSign={false}
                    withTrendArrow
                    positiveNegativeColor
                  />
                ) : (
                  "-"
                )}
              </Table.Column>
              <Table.Column>
                {position.net_pnl_percentage ? (
                  <Trend
                    value={position.net_pnl_percentage}
                    withSign={false}
                    withTrendArrow
                  />
                ) : (
                  "-"
                )}
              </Table.Column>
              <Table.Column>
                <BalancePercentage
                  balance={position.balance}
                  maxBalance={position.max_balance}
                />
              </Table.Column>
              <Table.Column>
                {position.duration ? (
                  <Time value={secondsAgoFromTimestamp(position.duration)} />
                ) : (
                  "-"
                )}
              </Table.Column>
            </Table.Row>
          );
        })
      )}
      {!isLoading && positions.length > ITEMS_PER_PAGE && (
        <Table.Row className="w-full h-[1.5rem] text-xs mt-[auto]">
          <Table.Column colSpan={7}>
            <Pagination
              page={currentPage}
              perPage={ITEMS_PER_PAGE}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              onNextPage={() => setCurrentPage(currentPage + 1)}
              onPreviousPage={() =>
                setCurrentPage(Math.max(currentPage - 1, 1))
              }
              label="Positions"
            />
          </Table.Column>
        </Table.Row>
      )}
      <TokenSellModal
        isOpen={sellModalOpen}
        toggleOpen={() => setSellModalOpen((prev) => !prev)}
        tokenId={
          selectedPosition ? `${selectedPosition.token_address}:${chain}` : null
        }
      />
      <TokenSendModal
        isOpen={sendModalOpen}
        toggleOpen={() => setSendModalOpen((prev) => !prev)}
        position={selectedPosition}
      />
    </>
  );
};

const PositionsTable = () => {
  const { currentWallet } = useWallet();

  const { sort, handleSortChange } = usePositionsFilter();

  const [currentPage, setCurrentPage] = useState(1);

  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tableRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  return (
    <Table ref={tableRef} className="h-full text-left text-xs pb-16">
      <colgroup>
        <col />
        <col style={{ width: "32%" }} />
      </colgroup>
      <Table.Header className="h-[1.5625rem] bg-black sticky top-0 z-10">
        <Table.HeaderColumn className="!pl-[0.625rem] w-[1.5625rem]">
          #
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div
            className="flex gap-1 items-center cursor-pointer"
            onClick={() => handleSortChange("token")}
          >
            Token
            {sort.sortField === "token" && (
              <div className="w-[0.75rem] h-[0.75rem] flex items-center">
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name={sort.sortType === "asc" ? "arrowUp" : "arrowDown"}
                  color="white"
                />
              </div>
            )}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div
            className="flex gap-1 items-center cursor-pointer"
            onClick={() => handleSortChange("invested")}
          >
            Invested
            {sort.sortField === "invested" && (
              <div className="w-[0.75rem] h-[0.75rem] flex items-center">
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name={sort.sortType === "asc" ? "arrowUp" : "arrowDown"}
                  color="white"
                />
              </div>
            )}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div
            className="flex gap-1 items-center cursor-pointer"
            onClick={() => handleSortChange("netPnl")}
          >
            Net PNL
            {sort.sortField === "netPnl" && (
              <div className="w-[0.75rem] h-[0.75rem] flex items-center">
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name={sort.sortType === "asc" ? "arrowUp" : "arrowDown"}
                  color="white"
                />
              </div>
            )}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div
            className="flex gap-1 items-center cursor-pointer"
            onClick={() => handleSortChange("netRoi")}
          >
            Net ROI
            {sort.sortField === "netRoi" && (
              <div className="w-[0.75rem] h-[0.75rem] flex items-center">
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name={sort.sortType === "asc" ? "arrowUp" : "arrowDown"}
                  color="white"
                />
              </div>
            )}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div
            className="flex gap-1 items-center cursor-pointer"
            onClick={() => handleSortChange("balance")}
          >
            Balance
            {sort.sortField === "balance" && (
              <div className="w-[0.75rem] h-[0.75rem] flex items-center">
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name={sort.sortType === "asc" ? "arrowUp" : "arrowDown"}
                  color="white"
                />
              </div>
            )}
          </div>
        </Table.HeaderColumn>
        <Table.HeaderColumn>
          <div
            className="flex gap-1 items-center cursor-pointer"
            onClick={() => handleSortChange("time")}
          >
            Time
            {sort.sortField === "time" && (
              <div className="w-[0.75rem] h-[0.75rem] flex items-center">
                <IconComponent
                  className="w-[0.375rem] h-[0.375rem]"
                  name={sort.sortType === "asc" ? "arrowUp" : "arrowDown"}
                  color="white"
                />
              </div>
            )}
          </div>
        </Table.HeaderColumn>
      </Table.Header>
      <Table.Body>
        {!currentWallet ? (
          <Table.Row>
            <Table.Column colSpan={7} className="!p-[0.625rem]">
              Sign in to view data
            </Table.Column>
          </Table.Row>
        ) : (
          <PositionsTableData
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        )}
      </Table.Body>
    </Table>
  );
};

export default PositionsTable;
